import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/Header';
import LumivetaLogo from '../../components/LumivetaLogo';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';

const SubscriptionPlans = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isPremium, isAdmin, isTrialing, canAccessApp, logout, refreshUser } = useAuth();
  const { isSpanish, language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [settings, setSettings] = useState({});
  const [fatalError, setFatalError] = useState(null);
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  
  // Welcome toast state
  const [showWelcomeToast, setShowWelcomeToast] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState('');

  // Check for welcome toast param (from registration)
  useEffect(() => {
    const welcome = searchParams.get('welcome');
    if (welcome === 'true') {
      const lang = searchParams.get('lang') || 'en';
      const message = lang === 'es'
        ? '¡Bienvenido! Sus Instrucciones de Operación de LUMIVETA™ han sido enviadas a su correo electrónico.'
        : 'Welcome! Your LUMIVETA™ Operating Instructions have been sent to your email.';
      setWelcomeMessage(message);
      setShowWelcomeToast(true);
      setTimeout(() => setShowWelcomeToast(false), 4000);
    }
  }, [searchParams]);

  // IMMEDIATE REDIRECT: Only for PAID premium users who are NOT trying to change plans
  const allowPlanChange = searchParams.get('change_plan') === 'true';
  useEffect(() => {
    if (isPremium && !isTrialing && !verifyingPayment && !allowPlanChange) {
      console.log('Paid premium user without change_plan param, redirecting to dashboard...');
      navigate('/main-dashboard');
    }
  }, [isPremium, isTrialing, verifyingPayment, navigate, allowPlanChange]);

  // Check for session_id in URL on page load (returning from Stripe)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      console.log('Session ID found in URL, verifying payment:', sessionId);
      verifyPaymentAndActivate(sessionId);
    }
  }, []);

  // Verify Stripe session and update user status
  const verifyPaymentAndActivate = async (sessionId) => {
    setVerifyingPayment(true);
    try {
      console.log('Verifying payment session:', sessionId);
      const result = await api.getPaymentStatus(sessionId);
      console.log('Payment verification result:', result);
      
      if (result.payment_status === 'paid') {
        setPaymentSuccess(true);
        
        if (refreshUser) {
          await refreshUser();
        }
        
        window.history.replaceState({}, document.title, window.location.pathname);
        
        console.log('Payment confirmed! Redirecting to dashboard...');
        setTimeout(() => {
          navigate('/main-dashboard');
        }, 1500);
      } else {
        console.log('Payment not yet confirmed, status:', result.payment_status);
        window.history.replaceState({}, document.title, window.location.pathname);
        if (refreshUser) {
          await refreshUser();
        }
      }
    } catch (error) {
      console.error('Error verifying payment:', error);
      window.history.replaceState({}, document.title, window.location.pathname);
      if (refreshUser) {
        await refreshUser();
      }
    } finally {
      setVerifyingPayment(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await api.getPublicSettings();
      setSettings(data);
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const handleSelectPlan = async () => {
    setFatalError(null);
    
    if (!settings.stripe_configured) {
      setFatalError('Payment system is not configured. Please contact lumiveta@yahoo.com');
      return;
    }

    setLoading(true);
    setRedirecting(true);
    
    try {
      // SELF-HEALING BILLING BRIDGE: Smart routing
      console.log('Smart upgrade for Premium Membership');
      const result = await api.smartUpgrade(language);
      console.log('Smart upgrade result:', result);
      
      const targetUrl = result.url;
      
      if (!targetUrl) {
        setRedirecting(false);
        setLoading(false);
        const errorDetail = result.detail || 'No URL returned from server';
        setFatalError(`UPGRADE FAILED: ${errorDetail}`);
        return;
      }
      
      // Accept both Stripe checkout URLs and billing portal URLs
      const isValidUrl = targetUrl.startsWith('https://checkout.stripe.com') || 
                        targetUrl.startsWith('https://billing.stripe.com');
      
      if (!isValidUrl) {
        setRedirecting(false);
        setLoading(false);
        setFatalError(`Invalid Stripe URL received: ${targetUrl}`);
        return;
      }
      
      console.log(`Redirecting to Stripe ${result.type}:`, targetUrl);
      
      try {
        window.top.location.href = targetUrl;
      } catch (frameError) {
        console.log('Frame navigation blocked, opening in new tab:', frameError.message);
        const stripeWindow = window.open(targetUrl, '_blank');
        if (!stripeWindow) {
          setRedirecting(false);
          setLoading(false);
          setFatalError('Popup blocked. Please allow popups for this site and try again.');
          return;
        }
        setRedirecting(false);
        setLoading(false);
      }
      
    } catch (error) {
      setRedirecting(false);
      setLoading(false);
      console.error('Error creating checkout:', error);
      setFatalError(`FATAL ERROR: ${error.message || 'Unknown error occurred'}`);
    }
  };

  const monthlyPrice = settings.monthly_price || 6.99;
  const subscriptionStatus = user?.subscription_status || 'free';
  const isPaidPremium = subscriptionStatus === 'active' || subscriptionStatus === 'premium';

  return (
    <div className="min-h-dvh bg-background">
      <Helmet>
        <title>{isSpanish ? 'Suscribirse a Premium' : 'Subscribe to Premium'} - Lumiveta</title>
        <meta name="description" content="Subscribe to Lumiveta Premium for scripture-grounded spiritual guidance." />
      </Helmet>
      
      {/* BRANDED OVERLAY - "Connecting to Secure Checkout..." */}
      {redirecting && !fatalError && (
        <div className="fixed inset-0 z-50 bg-background/95 flex flex-col items-center justify-center">
          <LumivetaLogo size="large" showText={false} />
          <h2 className="text-xl font-heading font-bold text-gold-gradient mt-6 mb-2">Lumiveta</h2>
          <div className="flex items-center gap-3 mt-4">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-foreground font-medium">
              {isSpanish ? 'Conectando al pago seguro...' : 'Connecting to Secure Checkout...'}
            </p>
          </div>
          <p className="text-muted-foreground text-sm mt-2">
            {isSpanish ? 'Por favor espera, serás redirigido a Stripe.' : 'Please wait, you will be redirected to Stripe.'}
          </p>
        </div>
      )}

      {/* PAYMENT VERIFICATION OVERLAY */}
      {(verifyingPayment || paymentSuccess) && !fatalError && (
        <div className="fixed inset-0 z-50 bg-background/95 flex flex-col items-center justify-center">
          <LumivetaLogo size="large" showText={false} />
          <h2 className="text-xl font-heading font-bold text-gold-gradient mt-6 mb-2">Lumiveta</h2>
          {paymentSuccess ? (
            <>
              <div className="flex items-center gap-3 mt-4">
                <Icon name="CheckCircle" size={24} color="#10B981" />
                <p className="text-green-400 font-medium text-lg">
                  {isSpanish ? '¡Pago Confirmado!' : 'Payment Confirmed!'}
                </p>
              </div>
              <p className="text-muted-foreground text-sm mt-2">
                {isSpanish ? 'Redirigiendo al panel principal...' : 'Redirecting to dashboard...'}
              </p>
            </>
          ) : (
            <>
              <div className="flex items-center gap-3 mt-4">
                <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <p className="text-foreground font-medium">
                  {isSpanish ? 'Verificando pago...' : 'Verifying payment...'}
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {/* FATAL ERROR OVERLAY */}
      {fatalError && (
        <div className="fixed inset-0 z-50 bg-background/95 flex flex-col items-center justify-center p-4">
          <div className="max-w-lg w-full bg-red-900/90 border-4 border-red-500 rounded-xl p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <Icon name="AlertTriangle" size={32} color="#EF4444" />
              <h2 className="text-2xl font-bold text-red-400">FATAL ERROR</h2>
            </div>
            <p className="text-white text-lg font-mono break-words mb-6" data-testid="fatal-error-message">
              {fatalError}
            </p>
            <div className="flex flex-col gap-3">
              <Button
                variant="outline"
                fullWidth
                onClick={() => {
                  setFatalError(null);
                  setLoading(false);
                  setRedirecting(false);
                }}
                className="border-red-500 text-red-400 hover:bg-red-500/20"
              >
                <Icon name="RefreshCw" size={16} className="mr-2" />
                {isSpanish ? 'Intentar de Nuevo' : 'Try Again'}
              </Button>
              <button
                onClick={() => {
                  localStorage.removeItem('lumiveta_token');
                  localStorage.removeItem('lumiveta_user');
                  sessionStorage.clear();
                  logout();
                  navigate('/login');
                }}
                className="text-sm text-red-300 hover:text-white transition-colors underline"
                data-testid="fatal-error-signout"
              >
                {isSpanish ? '← Cerrar sesión y salir' : '← Sign out and exit'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {canAccessApp && <Header />}
      
      <main className={`container mx-auto px-4 ${canAccessApp ? 'pt-24' : 'pt-8'} pb-16`}>
        <div className="max-w-2xl mx-auto">
          {/* Logo for non-subscribers */}
          {!canAccessApp && (
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <LumivetaLogo size="large" showText={false} />
              </div>
              <h1 className="text-2xl font-heading font-bold text-gold-gradient">Lumiveta</h1>
              <p className="text-muted-foreground text-sm">Light for your path</p>
            </div>
          )}

          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-gold-gradient mb-3">
              {isSpanish ? 'Comienza Tu Prueba Gratuita de 7 Días' : 'Start Your 7-Day Free Trial'}
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              {isSpanish 
                ? 'Orientación espiritual fundamentada en las Escrituras con la Reina-Valera 1909'
                : 'Scripture-grounded spiritual guidance with the World English Bible'}
            </p>
          </div>

          {/* Status Card for existing subscribers */}
          {(isPaidPremium || isAdmin) && (
            <div className="glass rounded-2xl p-6 mb-8 border border-primary/20">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <Icon name={isAdmin ? 'Shield' : 'Crown'} size={20} color="#FFD700" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">
                    {isAdmin 
                      ? (isSpanish ? 'Acceso de Administrador' : 'Admin Access')
                      : (isSpanish ? 'Miembro Premium' : 'Premium Member')}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {isAdmin 
                      ? (isSpanish ? 'Reflexiones ilimitadas' : 'Unlimited reflections')
                      : (isSpanish ? '7 reflexiones diarias' : '7 daily reflections')}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                fullWidth
                onClick={() => navigate('/main-dashboard')}
              >
                <Icon name="ArrowLeft" size={16} className="mr-2" />
                {isSpanish ? 'Volver al Dashboard' : 'Back to Dashboard'}
              </Button>
            </div>
          )}

          {/* SINGLE TIER PLAN CARD */}
          {!isPaidPremium && !isAdmin && (
            <div className="glass rounded-2xl overflow-hidden border border-primary/50 ring-2 ring-primary/30 mb-8">
              {/* Card Header */}
              <div className="p-6 bg-primary/10">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                    <Icon name="Crown" size={24} color="#FFD700" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-xl text-foreground">
                      {isSpanish ? 'Membresía Premium' : 'Premium Membership'}
                    </h3>
                    <span className="text-xs text-primary font-medium">
                      {isSpanish ? '7 Días Gratis' : '7 Days Free'}
                    </span>
                  </div>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-heading font-bold text-primary">$0.00</span>
                  <span className="text-muted-foreground">{isSpanish ? 'hoy' : 'today'}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {isSpanish 
                    ? `Después $${monthlyPrice}/mes` 
                    : `Then $${monthlyPrice}/mo after trial`}
                </p>
              </div>

              {/* Card Body */}
              <div className="p-6">
                <p className="text-sm text-muted-foreground mb-4">
                  {isSpanish 
                    ? '7 reflexiones diarias con guía espiritual completa'
                    : '7 Daily Reflections with full spiritual guidance'}
                </p>
                
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-2">
                    <Icon name="Check" size={16} color="#10B981" className="mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-foreground">
                      {isSpanish ? '7 reflexiones diarias' : '7 daily reflections'}
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Icon name="Check" size={16} color="#10B981" className="mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-foreground">
                      {isSpanish ? 'Pipeline completo de 7 partes' : 'Full 7-part response pipeline'}
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Icon name="Check" size={16} color="#10B981" className="mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-foreground">
                      {isSpanish ? 'Notas de Campo y audio' : 'Field Notes & audio'}
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Icon name="Check" size={16} color="#10B981" className="mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-foreground">
                      {isSpanish ? 'Soporte prioritario' : 'Priority support'}
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Icon name="Check" size={16} color="#10B981" className="mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-foreground">
                      {isSpanish ? 'Cancela en cualquier momento' : 'Cancel anytime'}
                    </span>
                  </li>
                </ul>

                <Button
                  variant="default"
                  fullWidth
                  onClick={handleSelectPlan}
                  disabled={loading}
                  className="gold-glow"
                  data-testid="subscribe-premium-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      {isSpanish ? 'Cargando...' : 'Loading...'}
                    </span>
                  ) : (
                    <>
                      <Icon name="Gift" size={18} className="mr-2" />
                      {isSpanish ? 'Comenzar Prueba Gratuita de 7 Días' : 'Start Your 7-Day Free Trial'}
                    </>
                  )}
                </Button>
                
                {/* Trust Blurb */}
                <p className="text-xs text-muted-foreground text-center mt-4 leading-relaxed">
                  {isSpanish 
                    ? '🔒 Se requiere tarjeta de crédito para verificar tu cuenta y prevenir spam de bots. Sin cargos por 7 días. Cancela en cualquier momento.'
                    : '🔒 Credit card required to verify account and prevent bot-spam. No charge for 7 days. Cancel anytime.'}
                </p>
              </div>
            </div>
          )}

          {/* Promo Code Info */}
          {!isPaidPremium && !isAdmin && (
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 glass rounded-full border border-primary/20">
                <Icon name="Tag" size={16} color="#FFD700" />
                <span className="text-sm text-muted-foreground">
                  {isSpanish 
                    ? '¿Tienes un código de descuento? Ingrésalo en el siguiente paso.' 
                    : 'Have a discount code? Enter it on the next step.'}
                </span>
              </div>
            </div>
          )}

          {/* FTC Compliance */}
          {!isPaidPremium && !isAdmin && (
            <p className="text-xs text-muted-foreground text-center max-w-2xl mx-auto leading-relaxed">
              {isSpanish 
                ? `Tu prueba gratuita de 7 días comienza hoy. Se te cobrará $${monthlyPrice}/mes después del período de prueba a menos que canceles. Cancela en cualquier momento desde tu configuración.`
                : `Your 7-day free trial starts today. You will be charged $${monthlyPrice}/mo after the trial period unless you cancel. Cancel anytime from your settings.`}
            </p>
          )}

          {/* Sign Out for non-subscribers */}
          {!canAccessApp && (
            <div className="text-center mt-6">
              <button
                onClick={() => {
                  localStorage.removeItem('lumiveta_token');
                  localStorage.removeItem('lumiveta_user');
                  sessionStorage.clear();
                  logout();
                  navigate('/login');
                }}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {isSpanish ? '← Cerrar sesión' : '← Sign out'}
              </button>
            </div>
          )}
        </div>
      </main>
      
      {/* Welcome Toast - z-[9999] to appear above FirstLoginDisclaimer modal */}
      {showWelcomeToast && (
        <div className="fixed top-4 right-4 z-[9999] animate-in slide-in-from-top-2 fade-in duration-300">
          <div className="bg-primary text-primary-foreground px-6 py-4 rounded-lg shadow-lg flex items-center gap-3 max-w-md">
            <Icon name="CheckCircle" size={24} />
            <p className="text-sm font-medium">{welcomeMessage}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPlans;
